// MF-67 — Generic staff/user picker (async roster search by code or last name), no per-row role.
// Used for "Reported by". Returns an array of { userId, code, name }.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, TextField, Chip } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { inputSx } from './formStyles'
import { getUsersSkipLimit } from '../../../../services/user/user.service'

const rowsFrom = (body) => {
  const data = body?.data ?? body
  if (Array.isArray(data)) return data
  return data?.paginatedResults ?? data?.users ?? data?.list ?? []
}

const toUser = (u) => ({
  userId: u._id,
  code: u.code || '',
  name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
})

const optionLabel = (u) => {
  const name = [u.lastName?.toUpperCase(), u.firstName].filter(Boolean).join(', ')
  return `${name || u.firstName || '—'}${u.code ? ` (${u.code})` : ''}`
}

const UserPicker = ({ value = [], onChange, placeholder = 'Search by surname or staff code…' }) => {
  const [input, setInput] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)

  const selectedIds = useMemo(() => new Set(value.map((s) => String(s.userId))), [value])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const term = input.trim()
    if (term.length < 2) {
      setOptions([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const [byCode, byLast] = await Promise.all([
          getUsersSkipLimit(0, 15, { code: term }).catch(() => null),
          getUsersSkipLimit(0, 15, { lastName: term }).catch(() => null)
        ])
        const merged = [...rowsFrom(byCode), ...rowsFrom(byLast)]
        const unique = Object.values(
          merged.reduce((acc, u) => {
            if (u?._id) acc[String(u._id)] = u
            return acc
          }, {})
        )
        setOptions(unique)
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => debounceRef.current && clearTimeout(debounceRef.current)
  }, [input])

  const add = (user) => {
    if (!user || selectedIds.has(String(user._id))) return
    onChange([...value, toUser(user)])
  }
  const remove = (i) => onChange(value.filter((_, si) => si !== i))

  return (
    <div className='flex flex-col' style={{ gap: 10 }}>
      <Autocomplete
        size='small'
        loading={loading}
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={optionLabel}
        getOptionDisabled={(o) => selectedIds.has(String(o._id))}
        inputValue={input}
        onInputChange={(_, v) => setInput(v)}
        value={null}
        blurOnSelect
        clearOnBlur
        onChange={(_, user) => {
          add(user)
          setInput('')
        }}
        noOptionsText={input.trim().length < 2 ? 'Type a name or code…' : 'No users found'}
        renderInput={(params) => <TextField {...params} placeholder={placeholder} sx={inputSx} />}
      />
      {value.length > 0 ? (
        <div className='flex flex-wrap' style={{ gap: 8 }}>
          {value.map((u, i) => (
            <Chip
              key={u.userId || u.name || i}
              label={
                <span>
                  {u.name}
                  {u.code ? <span style={{ color: 'rgb(100, 116, 139)' }}> · {u.code}</span> : null}
                </span>
              }
              onDelete={() => remove(i)}
              deleteIcon={<FontAwesomeIcon icon={['fas', 'xmark']} style={{ fontSize: 12 }} />}
              style={{ background: '#fff', border: '1px solid rgb(226, 232, 240)' }}
            />
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, color: 'rgb(148, 163, 184)' }}>No one added yet</span>
      )}
    </div>
  )
}

export default UserPicker
